import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Chip,
  Divider,
  Card,
  CardContent,
  FormHelperText,
} from '@mui/material';
import {
  LocalShipping as ShipIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config';
import { PRODUCTS, GRADES, BRANDS, PACK_SIZES, PACK_STYLES } from '../../utils/DROPDOWN_OPTIONS';

function MakeShipmentModal({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fclNoError, setFclNoError] = useState('');
  
  const [filters, setFilters] = useState({
    product: '',
    grade: '',
    brand: '',
    trace_id: '',
    day_code: '',
  });

  const [searchResults, setSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  
  const [shipmentDetails, setShipmentDetails] = useState({
    fcl_month: '',
    fcl_no: '',
    po_number: '',
    shipment_date: new Date().toISOString().split('T')[0],
  });

  const getFCLMonthOptions = () => {
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const options = [];
    const today = new Date();
    
    // ✅ Start from 1 month BEFORE current month (i = -1) to include previous month
    for (let i = -1; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const monthName = months[date.getMonth()];
      const year = date.getFullYear();
      options.push(`${monthName}-${year}`);
    }
    
    return options;
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        view: 'daily_inventory',
        ...filters,
      });

      const response = await axios.get(
        `${API_ENDPOINTS.INVENTORY}?${params}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setSearchResults(response.data.data || []);
        if (response.data.data.length === 0) {
          setError('No items found matching your search criteria');
        } else {
          setStep(2);
        }
      } else {
        setError('Failed to search inventory');
      }
    } catch (err) {
      console.error('Search failed:', err);
      setError('Failed to search inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fixed: Checkbox click handler that stops propagation
  const handleCheckboxClick = (e, item) => {
    e.stopPropagation(); // Prevent row click from firing
    handleSelectItem(item);
  };

  const handleSelectItem = (item) => {
    setSelectedItems((prev) => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSelectAll = (e) => {
    e.stopPropagation(); // Prevent any parent handlers
    if (selectedItems.length === searchResults.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems([...searchResults]);
    }
  };

  const getTotalCartons = () => {
    return selectedItems.reduce((sum, item) => sum + (item.cartons || 0), 0);
  };

  const getTotalLooseBags = () => {
    return selectedItems.reduce((sum, item) => sum + (item.loose_bags || 0), 0);
  };

  const getTotalKgs = () => {
    return selectedItems.reduce((sum, item) => sum + (parseFloat(item.total_kgs) || 0), 0);
  };

  const getTotalPounds = () => {
    return selectedItems.reduce((sum, item) => sum + (parseFloat(item.total_pounds) || 0), 0);
  };

  // ✅ FCL Number validation - Only 2 digits allowed (01-99), no alphabets
  const validateFclNo = (value) => {
    // Remove any non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 2 digits
    const limitedValue = digitsOnly.slice(0, 2);
    
    return limitedValue;
  };

  const handleShipmentDetailsChange = (field, value) => {
    if (field === 'fcl_no') {
      // Validate FCL number - only 2 digits allowed
      const validatedValue = validateFclNo(value);
      
      // Set error if user tries to enter invalid characters
      if (value !== validatedValue && value.length > 0) {
        setFclNoError('FCL Number must be 2 digits only (01-99), no alphabets allowed');
      } else if (validatedValue.length > 0 && validatedValue.length < 2) {
        setFclNoError('FCL Number must be exactly 2 digits (e.g., 01, 02, 10)');
      } else {
        setFclNoError('');
      }
      
      setShipmentDetails({
        ...shipmentDetails,
        [field]: validatedValue,
      });
    } else {
      setShipmentDetails({
        ...shipmentDetails,
        [field]: value,
      });
    }
  };

  // ✅ Check if FCL number is valid (exactly 2 digits)
  const isFclNoValid = () => {
    const fclNo = shipmentDetails.fcl_no;
    return fclNo && fclNo.length === 2 && /^\d{2}$/.test(fclNo);
  };

  const handleSubmit = async () => {
    if (!shipmentDetails.fcl_month) {
      setError('Please select FCL Month');
      return;
    }
    
    if (!isFclNoValid()) {
      setError('FCL Number must be exactly 2 digits (01-99)');
      setFclNoError('FCL Number must be exactly 2 digits (01-99)');
      return;
    }

    if (selectedItems.length === 0) {
      setError('Please select at least one item to ship');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const shipmentData = {
        fcl_month: shipmentDetails.fcl_month,
        fcl_no: shipmentDetails.fcl_no,
        po_number: shipmentDetails.po_number,
        shipment_date: shipmentDetails.shipment_date,
        inventory_ids: selectedItems.map(item => item.id),
      };

      console.log('Submitting shipment:', shipmentData);

      const response = await axios.post(
        API_ENDPOINTS.SHIPMENTS,
        shipmentData,
        { 
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        alert(`✅ Shipment created successfully!\n\nFCL: ${shipmentDetails.fcl_month} / ${shipmentDetails.fcl_no}\nItems shipped: ${selectedItems.length}\nTotal cartons: ${getTotalCartons()}\nTotal Pounds: ${getTotalPounds().toFixed(2)}`);
        onSuccess();
        handleClose();
      }
    } catch (err) {
      console.error('Failed to create shipment:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.error || 'Failed to create shipment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFilters({
      product: '',
      grade: '',
      brand: '',
      trace_id: '',
      day_code: '',
    });
    setSearchResults([]);
    setSelectedItems([]);
    setShipmentDetails({
      fcl_month: '',
      fcl_no: '',
      po_number: '',
      shipment_date: new Date().toISOString().split('T')[0],
    });
    setError('');
    setFclNoError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600 }}>
        <ShipIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Make Shipment Entry - Step {step} of 3
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* STEP 1: Search Filters */}
        {step === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Search Inventory to Ship
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use filters below to find items from Daily Inventory
            </Typography>

            <Grid container spacing={3}>
              {/* Row 1: Product, Grade, Brand */}
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Product"
                  value={filters.product}
                  onChange={(e) => handleFilterChange('product', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Products</MenuItem>
                  {PRODUCTS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Grade"
                  value={filters.grade}
                  onChange={(e) => handleFilterChange('grade', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Grades</MenuItem>
                  {GRADES.map((g) => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Brand"
                  value={filters.brand}
                  onChange={(e) => handleFilterChange('brand', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Brands</MenuItem>
                  {BRANDS.map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Row 2: Trace ID, Day Code */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Trace ID"
                  value={filters.trace_id}
                  onChange={(e) => handleFilterChange('trace_id', e.target.value)}
                  placeholder="e.g., AP051550/011"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Day Code"
                  value={filters.day_code}
                  onChange={(e) => handleFilterChange('day_code', e.target.value)}
                  placeholder="e.g., 5E27"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Button
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{ mt: 4, py: 1.5, fontSize: '1rem' }}
              fullWidth
            >
              {loading ? 'Searching...' : 'Search Inventory'}
            </Button>
          </Box>
        )}

        {/* STEP 2: Select Items */}
        {step === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Search Results ({searchResults.length} items found)
              </Typography>
              <Chip 
                label={`Selected: ${selectedItems.length} items`} 
                color={selectedItems.length > 0 ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ✅ Fixed: Scrollable table with both horizontal and vertical scroll */}
            <TableContainer 
              component={Paper} 
              sx={{ 
                maxHeight: 400, 
                overflow: 'auto',  // ✅ Enable both scrolls
              }}
            >
              <Table size="small" stickyHeader>
                {/* ✅ Fixed: Headers now visible with proper styling */}
                <TableHead>
                  <TableRow>
                    <TableCell 
                      padding="checkbox" 
                      sx={{ 
                        bgcolor: '#1565c0', 
                        position: 'sticky', 
                        top: 0, 
                        zIndex: 3 
                      }}
                    >
                      {/* ✅ Fixed: Select All checkbox */}
                      <Checkbox
                        checked={selectedItems.length === searchResults.length && searchResults.length > 0}
                        indeterminate={selectedItems.length > 0 && selectedItems.length < searchResults.length}
                        onChange={handleSelectAll}
                        sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                      />
                    </TableCell>
                    {/* ✅ Fixed: Pallet ID and Location first */}
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>Pallet ID</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>CS1</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>CS2</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>ID</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>Product</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>Grade</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>Brand</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>Pack Size</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }} align="right">Cartons</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }} align="right">Loose Bags</TableCell>
                    {/* ✅ Fixed: Show POUNDS not KGs */}
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }} align="right">Total LBS</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>Trace ID</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 }}>Day Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      selected={selectedItems.some(i => i.id === item.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&.Mui-selected': { bgcolor: 'success.light' },
                        '&.Mui-selected:hover': { bgcolor: 'success.light' },
                      }}
                      onClick={() => handleSelectItem(item)}
                    >
                      <TableCell padding="checkbox">
                        {/* ✅ Fixed: Checkbox click works independently */}
                        <Checkbox
                          checked={selectedItems.some(i => i.id === item.id)}
                          onClick={(e) => handleCheckboxClick(e, item)}
                        />
                      </TableCell>
                      {/* ✅ Fixed: Pallet ID and Location columns first */}
                      <TableCell><strong>{item.pallet_id || '-'}</strong></TableCell>
                      <TableCell>{item.cs1 || '-'}</TableCell>
                      <TableCell>{item.cs2 || '-'}</TableCell>
                      <TableCell>{item.id}</TableCell>
                      <TableCell><strong>{item.product}</strong></TableCell>
                      <TableCell>{item.grade}</TableCell>
                      <TableCell>{item.brand}</TableCell>
                      <TableCell>{item.pack_size}</TableCell>
                      <TableCell align="right"><strong>{item.cartons}</strong></TableCell>
                      <TableCell align="right">{item.loose_bags}</TableCell>
                      {/* ✅ Fixed: Show POUNDS */}
                      <TableCell align="right"><strong>{parseFloat(item.total_pounds || 0).toFixed(2)}</strong></TableCell>
                      <TableCell>{item.trace_id || '-'}</TableCell>
                      <TableCell>{item.day_code || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary Card */}
            {selectedItems.length > 0 && (
              <Card sx={{ mt: 3, bgcolor: 'success.light' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Selection Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Items Selected</Typography>
                      <Typography variant="h5" fontWeight="bold">{selectedItems.length}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Total Cartons</Typography>
                      <Typography variant="h5" fontWeight="bold">{getTotalCartons()}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Total Loose Bags</Typography>
                      <Typography variant="h5" fontWeight="bold">{getTotalLooseBags()}</Typography>
                    </Grid>
                    {/* ✅ Fixed: Show POUNDS */}
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Total Pounds (LBS)</Typography>
                      <Typography variant="h5" fontWeight="bold">{getTotalPounds().toFixed(2)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                fullWidth
              >
                Back to Search
              </Button>
              <Button
                variant="contained"
                onClick={() => setStep(3)}
                fullWidth
                disabled={selectedItems.length === 0}
              >
                Continue to Shipment Details
              </Button>
            </Box>
          </Box>
        )}

        {/* STEP 3: Shipment Details */}
        {step === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Enter Shipment Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label="FCL Month *"
                  value={shipmentDetails.fcl_month}
                  onChange={(e) => handleShipmentDetailsChange('fcl_month', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                >
                  {getFCLMonthOptions().map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="FCL Number *"
                  value={shipmentDetails.fcl_no}
                  onChange={(e) => handleShipmentDetailsChange('fcl_no', e.target.value)}
                  placeholder="e.g., 01, 02, 10"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    maxLength: 2,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                  error={!!fclNoError}
                  helperText={fclNoError || 'Enter 2 digits only (01-99). No alphabets allowed.'}
                  sx={{
                    '& input': {
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      letterSpacing: '0.2em'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PO Number"
                  value={shipmentDetails.po_number}
                  onChange={(e) => handleShipmentDetailsChange('po_number', e.target.value)}
                  placeholder="e.g., PO-2024-001"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Shipment Date"
                  value={shipmentDetails.shipment_date}
                  onChange={(e) => handleShipmentDetailsChange('shipment_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Final Summary */}
            <Card sx={{ mt: 3, bgcolor: 'info.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Shipment Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">FCL Month:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {shipmentDetails.fcl_month || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">FCL Number:</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ 
                      color: isFclNoValid() ? 'success.main' : 'error.main'
                    }}>
                      {shipmentDetails.fcl_no || '-'}
                      {shipmentDetails.fcl_no && !isFclNoValid() && (
                        <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                          (Invalid - must be 2 digits)
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">PO Number:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {shipmentDetails.po_number || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Shipment Date:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {shipmentDetails.shipment_date}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Items to Ship</Typography>
                    <Typography variant="h5" fontWeight="bold">{selectedItems.length}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Total Cartons</Typography>
                    <Typography variant="h5" fontWeight="bold">{getTotalCartons()}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Total Loose Bags</Typography>
                    <Typography variant="h5" fontWeight="bold">{getTotalLooseBags()}</Typography>
                  </Grid>
                  {/* ✅ Fixed: Show POUNDS */}
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Total Weight (LBS)</Typography>
                    <Typography variant="h5" fontWeight="bold">{getTotalPounds().toFixed(2)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setStep(2)}
                fullWidth
              >
                Back to Selection
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                fullWidth
                disabled={loading || !shipmentDetails.fcl_month || !isFclNoValid()}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
              >
                {loading ? 'Creating Shipment...' : 'Create Shipment'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          startIcon={<CloseIcon />}
          disabled={loading}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MakeShipmentModal;